import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import Input from '../../global/InputNew';
import { Button, BUTTON_SIZE, BUTTON_TYPE } from '../../global/Button';
import { capitalizeWords, isNullOrEmpty } from '../../utils/utils';
import { isZipValid, getUSStates, US_COUNTRY_CODE } from '../../utils/locations';
// import { normalizeUsPhoneDigits } from '../../utils/addresses';
import parsePlaceAddress from './placeAddressParser';

import './EnterAddress.scss';
import StateSelector from '../Register/Components/StateSelector';

const getDs20InputAutoComplete = (id) => {
  if (id === 'address') return 'street-address';
  // if (id === 'phone') return 'tel';
  return undefined;
};

const EnterAddress = ({
  addressSelected,
  setFromAddress,
  submitButtonText = 'SAVE DETAILS',
  variant = 'default',
  onValidAddressChange,
}) => {
  const isDs20 = variant === 'ds20';
  const stateSelected = getUSStates().find(({ code }) => code === addressSelected?.provinceCode);

  const [newAddress, setNewAddress] = useState(addressSelected?.address1 || '');
  const [firstName, setFirstName] = useState(capitalizeWords(addressSelected?.firstName || ''));
  const [lastName, setLastName] = useState(capitalizeWords(addressSelected?.lastName || ''));
  const [apartment, setApartment] = useState(addressSelected?.address2 || '');
  const [city, setCity] = useState(addressSelected?.city || '');
  const [selectedState, setSelectedState] = useState(stateSelected?.name || '');
  const [selectedStateCode, setSelectedStateCode] = useState(addressSelected?.provinceCode || '');
  const [zip, setZip] = useState(addressSelected?.zip || '');
  // Re-enable with phone + SMS UI when backend is ready:
  // const [phone, setPhone] = useState(addressSelected?.phone || '');
  // const [smsMarketingConsent, setSmsMarketingConsent] = useState(
  //   Boolean(addressSelected?.smsMarketingConsent),
  // );
  const [zipError, setZipError] = useState(false);
  const [shouldEnableSubmit, setShouldEnableSubmit] = useState(false);

  const autocompleteInput = useRef(null);
  const [sessionToken, setSessionToken] = useState(
    new window.google.maps.places.AutocompleteSessionToken(),
  );

  const handlePlaceChanged = (autocompleteObj) => {
    const place = autocompleteObj.getPlace();
    if (place) {
      const parsedPlace = parsePlaceAddress(place);
      setCity(parsedPlace.city);
      setSelectedState(parsedPlace.state);
      setSelectedStateCode(parsedPlace.stateCode);
      setNewAddress(parsedPlace.address1);
      setZip(parsedPlace.zip);
    }
    setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
  };

  useEffect(() => {
    const autocompleteObj = new window.google.maps.places.Autocomplete(
      autocompleteInput.current,
      {
        types: ['address'],
        sessionToken,
        componentRestrictions: { country: ['us'] },
      },
    );
    autocompleteObj.setFields(['address_components', 'name']);
    autocompleteObj.addListener('place_changed', () => handlePlaceChanged(autocompleteObj));

    return () => {
      window.google.maps.event.clearInstanceListeners(autocompleteObj);
    };
  }, []);

  const inputFieldsParameters = useMemo(() => [
    {
      id: 'firstName',
      placeholder: 'First name*',
      value: firstName,
      onChange: setFirstName,
    },
    {
      id: 'lastName',
      placeholder: 'Last name*',
      value: lastName,
      onChange: setLastName,
    },
    {
      id: 'address',
      placeholder: 'Address*',
      value: newAddress || '',
      onChange: setNewAddress,
      inputRef: autocompleteInput,
    },
    {
      id: 'complement',
      placeholder: 'Apartment, suite, etc. (optional)',
      value: apartment || '',
      onChange: setApartment,
    },
    {
      id: 'city',
      placeholder: 'City*',
      value: city || '',
      onChange: setCity,
    },
  ], [firstName, lastName, newAddress, apartment, city]);

  const isFormValid = useCallback(() => {
    const base = Boolean(
      firstName.length &&
      lastName.length &&
      !isNullOrEmpty(newAddress) &&
      !isNullOrEmpty(city) &&
      !isNullOrEmpty(zip) &&
      selectedStateCode?.length &&
      selectedState.length,
    );
    if (!base) {
      return false;
    }
    // if (isDs20) {
    //   return normalizeUsPhoneDigits(phone) != null;
    // }
    return true;
  }, [
    firstName,
    lastName,
    newAddress,
    city,
    zip,
    selectedStateCode,
    selectedState,
    // isDs20,
    // phone,
  ]);

  useEffect(() => {
    if (isZipValid(zip) || !zip) {
      setZipError(false);
    } else {
      setZipError(true);
      setShouldEnableSubmit(false);
    }
  }, [zip]);

  useEffect(() => {
    setShouldEnableSubmit(isFormValid());
  }, [
    firstName,
    lastName,
    newAddress,
    city,
    zip,
    selectedStateCode,
    selectedState,
    isFormValid,
  ]);

  const renderDs20TextField = (
    id,
    label,
    placeholder,
    fieldValue,
    onValueChange,
    inputRef = null,
    inputType = 'text',
  ) => (
    <div key={id} className="EnterAddress__dsField">
      <div
        className={classNames('EnterAddress__dsBlock', {
          'EnterAddress__dsBlock--error': id === 'zip' && zipError,
        })}
      >
        <div className="EnterAddress__dsNotch">
          <label htmlFor={id} className="EnterAddress__dsLabel">
            {label}
          </label>
        </div>
        <div
          className={classNames('EnterAddress__dsControl', {
            'EnterAddress__dsControl--error': id === 'zip' && zipError,
          })}
        >
          <input
            ref={inputRef}
            id={id}
            type={inputType}
            className="EnterAddress__dsInput"
            placeholder={placeholder}
            value={fieldValue || ''}
            onChange={(e) => onValueChange(e.target.value)}
            autoComplete={getDs20InputAutoComplete(id)}
            inputMode={id === 'phone' ? 'tel' : undefined}
          />
        </div>
      </div>
      {id === 'zip' && zipError && (
        <p className="EnterAddress__dsHelper EnterAddress__dsHelper--error">
          Invalid zip code
        </p>
      )}
    </div>
  );

  const buildAddressObject = useCallback(() => {
    if (!isFormValid() || !isZipValid(zip)) {
      return null;
    }
    const firstNameCaps = capitalizeWords(firstName);
    const lastNameCaps = capitalizeWords(lastName.trim());
    const cityCaps = capitalizeWords(city);
    const address1Caps = capitalizeWords(newAddress);
    // const phoneDigits = normalizeUsPhoneDigits(phone);
    return {
      firstName: firstNameCaps,
      lastName: lastNameCaps,
      address1: address1Caps,
      address2: apartment,
      city: cityCaps,
      provinceCode: selectedStateCode,
      zip: zip.replace(/\s/g, ''),
      countryCode: US_COUNTRY_CODE, // TODO: get from form when it's implemented
      // ...(isDs20 && phoneDigits
      //   ? {
      //     phone: `+1${phoneDigits}`,
      //     smsMarketingConsent,
      //   }
      //   : {}),
    };
  }, [
    isFormValid,
    zip,
    firstName,
    lastName,
    city,
    newAddress,
    apartment,
    selectedStateCode,
    // isDs20,
    // phone,
    // smsMarketingConsent,
  ]);

  const onSubmit = useCallback(() => {
    if (isFormValid() && isZipValid(zip)) {
      const addressObject = buildAddressObject();
      setFromAddress(addressObject);
      setZipError(false);
      setShouldEnableSubmit(true);
    } else if (!isZipValid(zip)) {
      setZipError(true);
      setShouldEnableSubmit(false);
    }
  }, [
    isFormValid,
    zip,
    buildAddressObject,
    setFromAddress,
    setZipError,
    setShouldEnableSubmit,
  ]);

  useEffect(() => {
    if (!onValidAddressChange) {
      return undefined;
    }
    const timer = setTimeout(() => {
      const built = buildAddressObject();
      onValidAddressChange(built);
    }, 400);
    return () => clearTimeout(timer);
  }, [
    onValidAddressChange,
    buildAddressObject,
    firstName,
    lastName,
    newAddress,
    apartment,
    city,
    zip,
    selectedStateCode,
    selectedState,
  ]);

  useEffect(() => {
    if (!onValidAddressChange) {
      return undefined;
    }
    return () => {
      onValidAddressChange(null);
    };
  }, [onValidAddressChange]);

  if (isDs20) {
    return (
      <div className="EnterAddress EnterAddress--ds20">
        <div className="EnterAddress__dsRow EnterAddress__dsRow--2 EnterAddress__dsRow--stack-sm">
          {renderDs20TextField('firstName', 'First name *', 'Jane', firstName, setFirstName)}
          {renderDs20TextField('lastName', 'Last name *', 'Doe', lastName, setLastName)}
        </div>
        {renderDs20TextField(
          'address',
          'Street address *',
          'Start typing your address',
          newAddress,
          setNewAddress,
          autocompleteInput,
        )}
        {renderDs20TextField(
          'complement',
          'Apt, suite (optional)',
          'Unit, apartment, etc.',
          apartment,
          setApartment,
        )}
        <div className="EnterAddress__dsRow EnterAddress__dsRow--3 EnterAddress__dsRow--stack-sm">
          {renderDs20TextField('city', 'City *', 'City name', city, setCity)}
          <StateSelector
            variant="ds20"
            selectedState={selectedState}
            setSelectedState={setSelectedState}
            selectedStateCode={selectedStateCode}
            setSelectedStateCode={setSelectedStateCode}
          />
          {renderDs20TextField('zip', 'ZIP code *', '12345', zip, setZip)}
        </div>
        {/* Re-enable when backend + product are ready (see commented state / buildAddressObject):
        <div className="EnterAddress__dsPhoneSms">
          {renderDs20TextField(
            'phone',
            'Phone',
            '+1 (XXX) XXX-XXXX',
            phone,
            setPhone,
            null,
            'tel',
          )}
          <label
            className="EnterAddress__dsSmsRow EnterAddress__dsSmsLabel"
            htmlFor="enterAddress-smsMarketing"
          >
            <input
              id="enterAddress-smsMarketing"
              type="checkbox"
              className="EnterAddress__dsCheckbox"
              checked={smsMarketingConsent}
              onChange={(e) => setSmsMarketingConsent(e.target.checked)}
            />
            Text me with news and offers
          </label>
        </div>
        */}
        {!onValidAddressChange && (
          <div className="EnterAddress__dsSubmitWrap">
            <Button
              type={BUTTON_TYPE.QUATERNARY}
              size={BUTTON_SIZE.LARGE}
              onClick={onSubmit}
              disabled={!shouldEnableSubmit || zipError}
              className="EnterAddress__dsSubmit ds20Btn ds20Btn--primary ds20Btn--block"
            >
              {submitButtonText}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="EnterAddress">
      {inputFieldsParameters.map((inputField) => (
        <div key={inputField.id}>
          <Input
            elementClassName="inputText"
            className="input"
            type="text"
            {...inputField}
            errorMessage=""
          />
        </div>
      ))}
      <StateSelector
        selectedState={selectedState}
        setSelectedState={setSelectedState}
        selectedStateCode={selectedStateCode}
        setSelectedStateCode={setSelectedStateCode}
      />
      <div className={`${zipError ? 'input-error' : ''}`}>
        <Input
          elementClassName="inputText"
          className={`input ${zipError ? 'input-error' : ''}`}
          type="text"
          id="zip"
          placeholder="Zip*"
          value={zip || ''}
          onChange={setZip}
          errorMessage=""
        />
        {zipError && (
          <div className="zip-error">
            <p>Invalid zip code</p>
          </div>
        )}
      </div>
      {!onValidAddressChange && (
        <div className="continue-button">
          <Button
            type={BUTTON_TYPE.QUATERNARY}
            size={BUTTON_SIZE.LARGE}
            onClick={onSubmit}
            disabled={!shouldEnableSubmit || zipError}
          >
            {submitButtonText}
          </Button>
        </div>
      )}

    </div>

  );
};

export default EnterAddress;
